/**
 * Groups API - List Groups
 *
 * GET /v1/groups
 */

import { z } from 'zod'
import { PaginationMetaSchema, PaginationParamsSchema } from '../common'
import {
  GroupThemeSchema,
  GroupSettingsAPISchema,
  GroupStreamChatSchema,
  GroupPermissionsSchema,
  GroupSchoolRefSchema,
  GroupSectionSchema,
  NotificationLevelSchema
} from './common'

// ============================================================================
// GET /v1/groups
// ============================================================================

export const GroupListRequestSchema = z.object({
  user_id: z.string().optional(),
  school_id: z.string().optional(),
  include_system_groups: z.boolean().optional(),
  include_non_system_groups: z.boolean().optional(),
  include_direct_messages: z.boolean().optional(),
  chat_groups_only: z.boolean().optional(),
  contact_enabled: z.boolean().optional(),
  audience: z.enum(['STUDENTS', 'PARENTS', 'FACULTY', 'STAFF', 'MIXED']).optional(),
  group_type: z
    .enum([
      'SCHOOL',
      'SCHOOL_LEVEL',
      'DEPARTMENT',
      'GRADE_LEVEL',
      'SECTION',
      'CUSTOM',
      'DIRECT_MESSAGE',
      'GROUP_MESSAGE',
      'CONTACT_INQUIRY'
    ])
    .optional(),
  // Matches the stored `Section.offeringType` literal `ACADEMIC` (not `ACADEMICS`)
  // so an explicit `offering_type=ACADEMIC` filter actually matches rows (SCH-210
  // U3 / SCH-215 — previously `ACADEMICS` matched zero sections).
  offering_type: z.enum(['ACADEMIC', 'ATHLETICS', 'ACTIVITIES', 'ADVISORIES', 'COMMUNITY_GROUPS']).optional(),
  school_year: z.string().optional(),
  ...PaginationParamsSchema.shape
})

export type GroupListRequest = z.infer<typeof GroupListRequestSchema>

export const GroupListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  displayName: z.string(),
  displayDescription: z.string().nullable(),
  status: z.string(),
  audience: z.string(),
  source: z.string(),
  groupType: z.string(),
  isSystemGroup: z.boolean(),
  showInApp: z.boolean(),
  betaTestersOnly: z.boolean(),
  memberCount: z.number(),
  theme: GroupThemeSchema.optional(),
  settings: GroupSettingsAPISchema,
  streamChat: GroupStreamChatSchema,
  permissions: GroupPermissionsSchema,
  school: GroupSchoolRefSchema,
  section: GroupSectionSchema,
  // Per-user membership on this group. `null` when the caller is not on the
  // roster (e.g. listing contact-target groups they may message). Mobile uses
  // this to filter out targets the user is themselves staff on.
  membership: z
    .object({
      identityRole: z.string(),
      sectionRole: z.string().nullable(),
      isLeader: z.boolean(),
      isHeadRole: z.boolean(),
      permissions: z.object({
        canView: z.boolean(),
        canReact: z.boolean(),
        canComment: z.boolean(),
        canPost: z.boolean(),
        canModerate: z.boolean(),
        canInvite: z.boolean()
      }),
      preferences: z.object({
        showInTimeline: z.boolean(),
        notifyLevel: NotificationLevelSchema,
        notifyFeed: z.boolean(),
        notifyPush: z.boolean(),
        notifyEmail: z.boolean(),
        notifySms: z.boolean()
      }),
      joinedAt: z.string(),
      lastInteractionAt: z.string().nullable()
    })
    .nullable()
    .optional(),
  // Contact-inquiry fields (present on CONTACT_INQUIRY groups, and contactEnabled
  // present on any contact-target group). awaitingStaffSince null means the most
  // recent message in this inquiry was from staff (no action needed).
  contactEnabled: z.boolean().optional(),
  awaitingStaffSince: z.string().nullable().optional(),
  contactTargetGroup: z
    .object({
      id: z.string(),
      name: z.string(),
      displayName: z.string().nullable().optional(),
      avatarImageUrl: z.string().nullable().optional()
    })
    .nullable()
    .optional(),
  // Announcement channel fields. `announcementMode` is true on broadcast groups
  // (only admins post, members read + react); defaults false / absent on every
  // other group. `announcementOriginGroup` is present on CONTACT_INQUIRY reply
  // groups created from an announcement, carrying the originating broadcast's id
  // + name so the staff inbox can render "Re: <announcement>".
  announcementMode: z.boolean().optional(),
  announcementOriginGroup: z
    .object({
      id: z.string(),
      name: z.string()
    })
    .nullable()
    .optional()
})

export type GroupListItem = z.infer<typeof GroupListItemSchema>

export const GroupListResponseSchema = z.object({
  data: z.array(GroupListItemSchema),
  meta: PaginationMetaSchema.optional(),
  message: z.string()
})

export type GroupListResponse = z.infer<typeof GroupListResponseSchema>

// Aliases
export const GetGroupsRequestSchema = GroupListRequestSchema
export const GetGroupsResponseSchema = GroupListResponseSchema
export type GetGroupsRequest = GroupListRequest
export type GetGroupsResponse = GroupListResponse
